import useOverlay, {OverlayState} from "@/hooks/common/useOverlay";
import Alert from "@COMPONENTS/common/modal/alert/Desktop";
import * as style from "@COMPONENTS/style/detailpackage/declaration";
import {TextButton} from "@FRAMEWORK/ui/desktop/button";
import {Textarea} from "@FRAMEWORK/ui/desktop/input";
import {BulletListItem} from "@FRAMEWORK/ui/desktop/list";
import {PopupContainer} from "@FRAMEWORK/ui/desktop/popup";
import {SelectBox, SelectLabel, SelectOptions} from "@FRAMEWORK/ui/desktop/select";
import {TableContainer} from "@FRAMEWORK/ui/desktop/table";
import usePortalCode from "@QUERIES/portal/usePortalCode";
import {reviewReportAsync, ReviewReportRequest} from "@QUERIES/portal/useReviewReport";
import {useQueryClient} from "@tanstack/react-query";
import React, {useCallback, useEffect, useId, useState} from "react";

interface ReportPopupProps extends OverlayState {
	/** 신고 게시글 ID */
	reportId: number | string;
	/** 신고 게시글 타입 */
	reportType: "REVIEW" | "EPISODE";
}

const debounce = (callback, delay) => {
	let timer;
	return (contents: string) => {
		clearTimeout(timer);
		timer = setTimeout(() => callback(contents), delay);
	};
};

const ReportPopup = (props: ReportPopupProps) => {
	const {reportId, reportType, ...overlayProps} = props;
	const queryClient = useQueryClient();
	const [report, setReport] = useState<ReviewReportRequest>({
		reportId: String(reportId),
		reportType,
		reportCode: "",
		reportContents: "",
	});

	const [open] = useOverlay();
	const {close} = props;
	const code = usePortalCode("REPORT_CODE");
	const list = code.data?.body || [];

	const input = useCallback(
		debounce((contents: string) => setReport({...report, reportContents: contents}), 500),
		[report],
	);

	const doConfirm = async () => {
		try {
			if (!report.reportContents) {
				open(overlay => <Alert text="신고 사유를 입력해주세요." {...overlay} />);
				return;
			}
			const result = await reviewReportAsync(report).catch(e => {
				throw new Error();
			});
			result.data.code === "0000" &&
				open(overlay => (
					<Alert
						text={"게시글 신고 접수가 완료 되었습니다.<br/> 접수하신 신고사유 검토하여 빠른 시일내에 처리하겠습니다."}
						{...overlay}
						resolve={value => {
							if (value === true) {
								reportType === "REVIEW" && queryClient.refetchQueries({queryKey: ["useReview"], type: "active"});
								reportType === "EPISODE" &&
									queryClient.refetchQueries({queryKey: ["useReviewEpisodeDetail"], type: "active"});
								close();
							}
						}}
					/>
				));
			result.data.code !== "0000" &&
				open(overlay => (
					<Alert
						text={result.data.message.replace(/\n/g, "<br/>")}
						{...overlay}
						resolve={value => {
							if (value === true) {
								close();
							}
						}}
					/>
				));
		} catch (e) {
			open(prop => <Alert {...prop} text="오류가 발생하였습니다." />);
		}
	};

	// 신고 사유 콤보 박스
	const id = useId();
	const [showCode, setShowCode] = useState(false);
	const [currentCode, setCurrentCode] = useState({key: "", text: ""});
	const handleSelect = e => {
		setCurrentCode({key: e.target.getAttribute("value"), text: e.target.innerText});
		setReport({...report, reportCode: e.target.getAttribute("value")});
	};

	useEffect(() => {
		if (code.isFetching) return;
		const {commCdId, commCdNm} = list.at(0);
		setCurrentCode({key: commCdId, text: commCdNm});
		setReport({...report, reportCode: commCdId});
	}, [code.isFetching]);

	useEffect(() => {
		if (!showCode) return;
		const event = e => {
			if (document.body.contains(e.target)) {
				!e.target.closest(`#${CSS.escape(id)}`) && setShowCode(false);
			}
		};
		setTimeout(() => window.addEventListener("click", event));
		return () => {
			window.removeEventListener("click", event);
		};
	}, [showCode]);

	return (
		<PopupContainer {...overlayProps} title="신고하기" size="regular" paddingTop={false}>
			<TableContainer lineTop={false}>
				<caption>신고하기</caption>
				<colgroup>
					<col style={{width: "170px"}} />
					<col style={{width: "550px"}} />
				</colgroup>
				<tbody>
					<tr>
						<th>신고사유</th>
						<td>
							<style.SelectWrap id={id}>
								<SelectBox onClick={() => setShowCode(!showCode)} tabIndex={1}>
									<SelectLabel>{currentCode.text}</SelectLabel>
									{showCode && (
										<SelectOptions search>
											{list.map(({commCdId, commCdNm}) => (
												<li onClick={handleSelect} key={commCdId} value={commCdId}>
													{commCdNm}
												</li>
											))}
										</SelectOptions>
									)}
								</SelectBox>
							</style.SelectWrap>
						</td>
					</tr>
					<tr>
						<td colSpan={2}>
							<style.TextareaWrap>
								<Textarea
									cols={30}
									placeholder={`신고 사유 항목에 없거나 추가로 신고하시고자 하는 내용을 작성해주세요.\n단, 신고내용 외 개인정보(연락처, 주소 등)는 절대 입력하지 마세요.`}
									onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => input(e.target.value)}
								/>
							</style.TextareaWrap>
						</td>
					</tr>
				</tbody>
			</TableContainer>
			<style.BulletList>
				<BulletListItem>신고해 주신 내용은 관리자 검토 후 내부정책에 의거 조치가 진행됩니다.</BulletListItem>
			</style.BulletList>
			<style.ButtonBlock>
				<TextButton text="취소" types="basic" size="submit" width="basic" onClick={close} />
				<TextButton text="신고" types="confirm" size="submit" width="basic" onClick={doConfirm} />
			</style.ButtonBlock>
		</PopupContainer>
	);
};

export default ReportPopup;
